import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { context } from '@adornis/baseql/server/context.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { BinaryBlob } from '@adornis/collab/db/scalars.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { Names } from '@ils/static/names.js';
import { DateTime } from 'luxon';
import type { InsertOneResult, UpdateResult } from 'mongodb';
import { answerAfterUpdate, filesPresentResolver } from '../server/resolver/answer.js';
import { ILSWebmFile } from './ils-files.js';

export type AnswerError =
  | 'missing-trainee'
  | 'missing-combined'
  | 'missing-both'
  | 'broken-trainee'
  | 'broken-combined'
  | 'broken-both'
  | 'broken-text';

@Entity()
export class AnswerComment extends AdornisEntity {
  static override _class = Names.AnswerComment._class;

  static override resolveID(entity: any): string {
    return A.getGloballyUniqueID();
  }

  @Field(type => String)
  text?: string;

  @Field(type => String)
  testerID?: string;
}

@Entity()
export class AnswerErrorReport extends AdornisEntity {
  static override _class = Names.AnswerErrorReport._class;

  static override resolveID(entity: any): string {
    return A.getGloballyUniqueID();
  }

  @validate(nonOptional())
  @Field(type => String)
  errorType!: AnswerError;

  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  reportedAt!: DateTime;

  @validate(nonOptional())
  @Field(type => String)
  reportedBy!: string;

  @Field(type => String)
  description?: string;

  @Field(type => String)
  locationID?: Maybe<string>;
}

@Entity()
export class Answer extends MongoEntity {
  static override _class = Names.Answer._class;
  static override _collectionName = Names.Answer._collectionName;

  static override get allFields() {
    return selectionSet(() => Answer, 1);
  }

  @Field(type => ID)
  slotID!: string;

  @Field(type => String)
  type!: 'text' | 'voice';

  @Field(type => String)
  text!: string;

  @Field(type => BinaryBlob)
  textYjs: Maybe<Uint8Array>;

  @Field(type => String)
  fileId?: string;

  @Field(type => String)
  onlyTraineeFileId?: string;

  @Field(type => [AnswerComment])
  comments: Maybe<AnswerComment[]>;

  @Field(type => AnswerErrorReport)
  reportedError: Maybe<AnswerErrorReport>;

  @DataLoadedField(type => ILSWebmFile, (entity: Answer) => entity.fileId)
  file?: ILSWebmFile;

  @Field(type => Boolean, {
    resolve(this: Answer) {
      return () => filesPresentResolver(this.fileId, this.onlyTraineeFileId);
    },
  })
  filesPresent?: boolean;

  isValid() {
    return this.type === 'voice'
      ? !!this.fileId &&
          this.fileId !== 'pending' &&
          !!this.onlyTraineeFileId &&
          this.onlyTraineeFileId !== 'pending' &&
          this.filesPresent &&
          !this.reportedError
      : !!this.text;
  }

  static override async afterCreate(entity: MongoEntity, result: InsertOneResult): Promise<void> {
    // make answers visible for creator
    if (!context.serverContext) await context.recreate();

    return super.afterCreate(entity, result);
  }

  static override async afterUpdate(
    oldEntity: { _id?: string | undefined; _class?: string | undefined },
    newEntity: Answer,
    result: UpdateResult,
  ): Promise<void> {
    await super.afterUpdate(oldEntity, newEntity, result);
    await answerAfterUpdate(oldEntity, newEntity, result);
  }
}
